import { ACTIVITY_FEED_MAPPING_SCHEMA } from '@warebee/shared/import-converter';
import { InferResult, QueryCreator, sql } from 'kysely';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import { DatasetDatabase, datasetQueryBuilder } from '../datasetQueryBuilder';
import {
  applyShiftFilter,
  applyTimeFilter,
} from '../datasetQueryBuilder.helper';
import {
  ActualityBaseQueryBuilderParams,
  DATASET_QUERY_VALUE_CONCATENATION,
} from '../datasetQueryBuilder.types';

export const ACTIVITY_FEED_EXTRA_FIELD_PREFIX = 'af_extra';

const activityFeedAllFieldsStub = (params: ActualityBaseQueryBuilderParams) =>
  getActivityFeedQueryBuilder(params, datasetQueryBuilder)
    .selectFrom('af')
    .selectAll();

export type ActivityFeedDataRows = InferResult<
  ReturnType<typeof activityFeedAllFieldsStub>
>;

export type ActivityFeedDataRow = ActivityFeedDataRows[number];
export type ActivityFeedDataColumn = keyof ActivityFeedDataRow;

export function getActivityFeedQueryBuilder<T extends DatasetDatabase>(
  params: ActualityBaseQueryBuilderParams,
  db: QueryCreator<DatasetDatabase>,
) {
  const { documentContext } = params;
  const activityParams = params.activity;
  const hasAgentTypeFilter = !_.isEmpty(activityParams?.disabledAgentTypes);
  const hasProcessTypeFilter = !_.isEmpty(activityParams?.disabledProcessTypes);
  const hasAgentFilter = !_.isNil(activityParams?.agentId);

  let at = db
    .with(
      cte => cte('af_pre'),
      db => {
        let qb = db
          .selectFrom('___activity_feed___ as ae')

          .select(({ fn }) => [
            sql<string>`${sql.ref('consignee')} || '${sql.raw(DATASET_QUERY_VALUE_CONCATENATION)}' || ${sql.ref('sku')} `.as(
              'skuKey',
            ),
            sql<string>`${sql.ref('locationId')} || '${sql.raw(DATASET_QUERY_VALUE_CONCATENATION)}' || ${sql.ref('consignee')} || '${sql.raw(DATASET_QUERY_VALUE_CONCATENATION)}'|| ${sql.ref('sku')} `.as(
              'activityItem',
            ),
            sql<string>`${sql.ref('jobId')} || '${sql.raw(DATASET_QUERY_VALUE_CONCATENATION)}' || ${sql.ref('agentId')}|| '${sql.raw(DATASET_QUERY_VALUE_CONCATENATION)}' || ${sql.ref('eventProcessType')} `.as(
              'jobKey',
            ),
            ..._(ACTIVITY_FEED_MAPPING_SCHEMA.fields)
              .map(f => f.name)
              .value(),

            ..._.map(activityParams?.extraFields, fd =>
              sql<
                number | string | Date | boolean
              >`json_value(${sql.ref('raw_data')}, 'strict $."${sql.raw(fd.name)}"' returning ${sql.raw(fd.type?.[0])})`.as(
                (fd.alias ?? `afExtra${nanoid(8)}`) as `afExtra${string}`,
              ),
            ),
          ])

          //  WHERE
          .where('ae.datasetObjectId', '=', activityParams.datasetId as any) //TODO: Kysely generic db issue;

          .$if(hasAgentTypeFilter, db =>
            db.where(
              'ae.agentType',
              'not in',
              activityParams.disabledAgentTypes as any,
            ),
          )
          .$if(hasProcessTypeFilter, db =>
            db.where(
              'ae.eventProcessType',
              'not in',
              activityParams.disabledProcessTypes as any,
            ),
          )
          .$if(hasAgentFilter, db =>
            db.where('ae.agentId', '=', activityParams.agentId as any),
          );

        qb = applyShiftFilter({
          qb,
          documentContext,
          field: 'eventEndTime',
        });
        return qb;
      },
    )
    .with(
      cte => cte('jobIds'),
      db => {
        let qb = db
          .selectFrom('af_pre')
          .select(({ fn }) => [
            `jobKey as targetJobKey`,
            fn.count<number>('eventId').as('timeFramedEventsCount'),
            fn.sum<number>('quantity').as('timeFramedUomCount'),
          ])
          .groupBy(['jobKey']);

        qb = applyTimeFilter({ qb, documentContext, field: 'eventEndTime' });
        return qb;
      },
    )
    .with(
      cte => cte('af'),
      db => {
        let qb = db
          .selectFrom('af_pre as afp')
          .selectAll('afp')
          .select(({ fn }) => [
            sql<number>`${sql.ref('timeFramedEventsCount')}`.as(
              'timeFramedEventsCount',
            ),
            sql<number>`${sql.ref('timeFramedUomCount')}`.as(
              'timeFramedUomCount',
            ),
          ]);

        if (activityParams.applyTimeFilterToJobs) {
          qb = qb.innerJoin('jobIds', join =>
            join.onRef('jobKey', '=', 'targetJobKey'),
          );
        } else {
          qb = qb.leftJoin('jobIds', join =>
            join.onRef('jobKey', '=', 'targetJobKey'),
          );
          qb = applyTimeFilter({ qb, documentContext, field: 'eventEndTime' });
        }

        return qb;
      },
    );

  return at;
}
